
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

export const useSimpleAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // Set timeout for auth initialization
        const timeoutId = setTimeout(() => {
          if (mounted) {
            console.warn('⚠️ useSimpleAuth: Authentication timeout, proceeding with no user');
            setLoading(false);
            setError('Authentication timeout');
          }
        }, 5000);

        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (mounted) {
          clearTimeout(timeoutId);
          
          if (sessionError) {
            console.error('🔥 useSimpleAuth: Session error:', sessionError);
            setError('Authentication error');
          } else {
            setSession(session);
            setUser(session?.user ?? null);
          }
          
          setLoading(false);
        }
      } catch (error) {
        if (mounted) {
          console.error('🔥 useSimpleAuth: Init error:', error);
          setError('Authentication failed');
          setLoading(false);
        }
      }
    };

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (mounted) {
          console.log('🔄 useSimpleAuth: Auth state changed:', event);
          setSession(session);
          setUser(session?.user ?? null);
          setError(null);
          setLoading(false);
        }
      }
    );

    initAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { user, session, loading, error };
};
